import { useI18n } from '@ecom/i18n';
import { Badge, type BadgeTone, Button, Card } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import {
  problemMessage,
  showsProblem,
  STATE_LABEL_KEY,
  STATE_TEXT_KEY,
  STATE_TONE,
  testAvailability,
} from '../state';
import type { JibexAccountView } from '../types';

import { formatDateTime } from './format';

import { AbouebMark } from '@/components/ui/AbouebMark';

/** The drawn link's line and knot per verdict tone (literal classes for Tailwind's JIT). */
const LINK_LINE: Record<BadgeTone, string> = {
  good: 'border-solid border-good',
  warn: 'border-dashed border-warn',
  critical: 'border-dashed border-critical',
  info: 'border-dashed border-info',
  accent: 'border-dashed border-accent',
  neutral: 'border-dashed border-inkFaint',
};
const LINK_KNOT: Record<BadgeTone, string> = {
  good: 'bg-good text-white',
  warn: 'bg-warn text-white',
  critical: 'bg-critical text-white',
  info: 'bg-info text-white',
  accent: 'bg-accent text-white',
  neutral: 'bg-inkFaint text-white',
};

function LinkGlyph({ tone }: { tone: BadgeTone }): ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      className="h-3 w-3"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {tone === 'good' ? <path d="m4 8.5 2.5 2.5L12 5.5" /> : null}
      {tone === 'critical' ? <path d="M5 5l6 6M11 5l-6 6" /> : null}
      {tone === 'warn' ? <path d="M8 4.5v4.5M8 11.5v.01" /> : null}
      {tone === 'info' || tone === 'accent' || tone === 'neutral' ? (
        <path d="M8 4.5V8l2.5 1.5" />
      ) : null}
    </svg>
  );
}

/**
 * « Statut du compte » — the one place the merchant reads whether Jibex is connected: a coloured badge, one sentence
 * in plain words, the reason when there is one, when it was last checked, and the test button.
 *
 * The badge is never the only carrier of the message (colour alone is not accessible): the label and the sentence say
 * the same thing in words, and the block is a polite live region so a screen reader announces a new verdict.
 */
export function JibexStatusCard({
  view,
  formIsDirty,
  testing,
  onTest,
  notice,
}: {
  readonly view: JibexAccountView;
  readonly formIsDirty: boolean;
  readonly testing: boolean;
  readonly onTest: () => void;
  /** A message about the last attempt that never reached the account (limit, outage) — already translated. */
  readonly notice: string | null;
}): ReactElement {
  const { t, locale } = useI18n();
  const availability = testAvailability(view, formIsDirty, testing);
  const problem = showsProblem(view.state) ? problemMessage(view.problemCode) : null;

  const tone = STATE_TONE[view.state];
  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-ink">
          {t('merchant.delivery.account.status.title')}
        </h2>
        <Badge tone={tone}>{t(STATE_LABEL_KEY[view.state])}</Badge>
      </div>

      {/* The link itself, drawn: ABOUEB on one side, Jibex on the other, the line between them solid when they talk,
          dashed and coloured when they do not. Decoration — the badge and the sentence carry the same verdict. */}
      <div
        className="flex items-center gap-3 rounded-xl bg-surface2 px-4 py-3.5"
        aria-hidden="true"
      >
        <AbouebMark className="h-9 w-9 shrink-0" />
        <span className="relative flex flex-1 items-center">
          <span
            key={view.state}
            className={`anim-grow-x block w-full border-t-2 ${LINK_LINE[tone]}`}
          />
          <span
            className={`absolute start-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border-2 border-surface2 rtl:translate-x-1/2 ${LINK_KNOT[tone]}`}
          >
            <LinkGlyph tone={tone} />
          </span>
        </span>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-surface font-display text-base font-semibold text-ink shadow-[0_0_0_1px_var(--ecom-line)]">
          J
        </span>
      </div>

      <div role="status" aria-live="polite" className="flex flex-col gap-2">
        <p className="text-sm text-ink">{t(STATE_TEXT_KEY[view.state])}</p>
        {problem !== null ? (
          <p className="text-sm text-inkSoft">{t(problem.key, problem.params)}</p>
        ) : null}
        {notice !== null ? (
          <p role="alert" className="rounded-xl bg-warnSoft px-3.5 py-2.5 text-sm text-warn">
            {notice}
          </p>
        ) : null}
      </div>

      {view.account !== null ? (
        <dl className="grid gap-1 text-xs text-inkFaint sm:grid-cols-2">
          <div>
            <dt className="sr-only">{t('merchant.delivery.account.form.clientIdLabel')}</dt>
            <dd>
              {t('merchant.delivery.account.status.clientId', { clientId: view.account.clientId })}
            </dd>
          </div>
          <div>
            <dt className="sr-only">{t('merchant.delivery.account.history.colDate')}</dt>
            <dd>
              {view.lastCheckAt === null
                ? t('merchant.delivery.account.status.neverChecked')
                : t('merchant.delivery.account.status.lastCheck', {
                    date: formatDateTime(view.lastCheckAt, locale),
                  })}
            </dd>
          </div>
        </dl>
      ) : null}

      {!view.platformReady ? (
        <p className="rounded-xl bg-infoSoft px-3.5 py-2.5 text-sm text-info">
          {t('merchant.delivery.account.platformNotReady')}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={onTest}
          loading={testing}
          disabled={!availability.allowed}
        >
          {testing
            ? t('merchant.delivery.account.test.running')
            : t('merchant.delivery.account.test.button')}
        </Button>
        {!availability.allowed && availability.reasonKey !== undefined && view.platformReady ? (
          <span className="text-xs text-inkFaint">{t(availability.reasonKey)}</span>
        ) : null}
      </div>
    </Card>
  );
}
